/**
 * ThankYouPage.js
 *
 * /fly/thank-you — after the party: a note from Captain Yash on Right Brother Airways stationery,
 * then airport signs to the souvenirs, the customs declarations, the photos (Baggage Claim) and the exit.
 * Public: nothing private here.
 */

import React from "react";
import { Camera, Circle, Plane } from "lucide-react";
import { AfterPartySigns, useGuestColour } from "./components/AfterParty";
import { useNoIndex } from "./helpers/flyPage";

const INK = "#1B2A41";
const MUTED = "#5B6577";
const PAPER = "#FBF7EF";
const BACKDROP = "#E9E4DA";
const ACCENT = "#007CA0";
const MONO = "'JetBrains Mono', monospace";

/** A pilot's wings, the captain's badge on the note */
const Wings = ({ colour }) => (
    <svg width="132" height="40" viewBox="0 0 132 40" fill="none" aria-hidden="true">
        <path d="M54 20c-10-1-22-4-34-10C12 6 6 4 2 4c6 5 12 9 20 12 9 4 20 6 32 7z" fill={colour} />
        <path d="M54 25c-9 0-18-2-28-5 7 4 15 7 28 8z" fill={colour} opacity=".7" />
        <path d="M78 20c10-1 22-4 34-10 8-4 14-6 18-6-6 5-12 9-20 12-9 4-20 6-32 7z" fill={colour} />
        <path d="M78 25c9 0 18-2 28-5-7 4-15 7-28 8z" fill={colour} opacity=".7" />
        <circle cx="66" cy="22" r="12" fill={colour} />
        <text x="66" y="26.5" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="11" fontWeight="700" fill={PAPER}>YJ</text>
    </svg>
);

const MemoRow = ({ label, children }) => (
    <div className="grid grid-cols-[72px_1fr] gap-3 items-baseline">
        <span className="text-[10px] font-bold tracking-[0.2em]" style={{ color: MUTED }}>{label}</span>
        <span className="text-[13px] sm:text-sm font-bold" style={{ fontFamily: MONO }}>{children}</span>
    </div>
);

const ThankYouPage = () => {
    useNoIndex("A note from the captain · Flight YJ030");
    const colour = useGuestColour();

    return (
        <div className="min-h-screen w-full pb-14" style={{ background: BACKDROP, color: INK, fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
            <div className="mx-auto w-full max-w-xl px-3 pt-8 sm:pt-14 flex flex-col gap-8">

                <div className="flex items-center justify-center gap-2 text-xs font-bold tracking-[0.24em]" style={{ color: MUTED }}>
                    <Plane size={14} aria-hidden="true" /> RIGHT BROTHER AIRWAYS
                </div>

                {/* ---------- The captain's note ---------- */}
                <article className="relative text-left rounded-[6px] px-5 pt-7 pb-8 sm:px-9 sm:pt-9" style={{ background: PAPER, boxShadow: "0 10px 30px rgba(27,42,65,0.18)", borderTop: `6px solid ${ACCENT}` }}>
                    {/* The arrival stamp, in the note's top corner */}
                    <div className="absolute right-3 top-4 sm:right-6 sm:top-6 grid place-items-center rounded-full text-center"
                        style={{ width: 74, height: 74, border: "3px double #B5192E", color: "#B5192E", transform: "rotate(-14deg)", opacity: 0.75 }} aria-hidden="true">
                        <div className="text-[8px] font-bold tracking-[0.14em] leading-tight">ARRIVED<br /><span style={{ fontFamily: MONO, fontSize: 13 }}>BAH</span><br />30.10.26</div>
                    </div>
                    <div className="flex flex-col items-center gap-2 text-center">
                        <Wings colour={ACCENT} />
                        <div className="text-[11px] font-bold tracking-[0.3em]" style={{ color: ACCENT }}>FROM THE FLIGHT DECK</div>
                    </div>

                    <div className="mt-6 flex flex-col gap-1.5 border-y border-dashed py-4" style={{ borderColor: "#C9C2B4" }}>
                        <MemoRow label="TO">ALL PASSENGERS</MemoRow>
                        <MemoRow label="FROM">CAPTAIN YASH</MemoRow>
                        <MemoRow label="FLIGHT">YJ030 · 30 OCT 2026</MemoRow>
                        <MemoRow label="STATUS">
                            <span className="inline-flex items-center gap-1.5" style={{ color: "#1F7A4A" }}><Circle size={8} fill="currentColor" strokeWidth={0} aria-hidden="true" />LANDED</span> AT BAH
                        </MemoRow>
                    </div>

                    <h1 className="mt-6 text-[30px] sm:text-4xl font-bold leading-[1.1] tracking-tight">Thank you for flying Right Brother Airways.</h1>

                    <div className="mt-5 flex flex-col gap-4 text-[15px] sm:text-base leading-relaxed">
                        <p>Hey everyone,</p>
                        <p>This is your captain speaking. Flight YJ030 has officially landed.</p>
                        <p>
                            Thank you so much for flying with Right Brother Airways. I hope the flight was as much fun for
                            you as it was for me. Turning 26 with all of you on board is something I'll remember for a long time.
                        </p>
                        <p>
                            Before you deplane, please don't forget to collect your <strong>baggage</strong> (all the photos from the
                            night) and your <strong>souvenirs</strong> (your boarding pass and passport, yours to keep). Just tap the
                            signs on the card attached.
                        </p>
                        {/* Highlighted in the guest's colour (the airline's blue when signed out) */}
                        <div className="flex gap-3 rounded-xl px-4 py-3.5" style={{ background: `${colour}1F`, borderLeft: `4px solid ${colour}` }}>
                            <Camera size={22} color={colour} strokeWidth={2.2} className="mt-0.5 shrink-0" aria-hidden="true" />
                            <p className="font-medium" style={{ color: INK }}>
                                <strong>Took photos on the flight? Please add yours to the album too.</strong> Every photo you
                                upload at <strong>Baggage Claim</strong> helps everyone relive the night.
                            </p>
                        </div>
                        <p>
                            I hope to welcome you on board again next year. Until then, keep your seatbelts loosely
                            fastened and your passports ready.
                        </p>
                        <p>With love from the flight deck,</p>
                    </div>

                    <div className="mt-2">
                        <div className="text-[34px] leading-none" style={{ fontFamily: "'Dancing Script', cursive", color: ACCENT }}>Captain Yash</div>
                        <div className="mt-2 text-[10px] font-bold tracking-[0.2em]" style={{ color: MUTED }}>CAPTAIN · FLIGHT YJ030</div>
                    </div>

                </article>

                {/* ---------- Where to next ---------- */}
                <AfterPartySigns current="note" colour={colour} />
            </div>
        </div>
    );
};

export default ThankYouPage;
